// ---------------------------------------------------------------------------
// Generation Service
// ---------------------------------------------------------------------------
// Orchestrates AI page generation:
//   1. Assembles system prompt (static + brand + field registry + page type)
//   2. Sends request to AI gateway (api-ai worker)
//   3. Validates AI output against Puck JSON schema
//   4. Runs sanitization (strip XSS)
//   5. Applies auto-wiring (map form fields to CRM)
//   6. Returns validated Puck JSON + bindings
//
// Does NOT call the AI provider directly — goes through the api-ai gateway.
// Returns ServiceResult<T> — { data } on success, { error, message } on failure.

import { z } from "zod";
import type {
  ServiceResult,
  EmitEventInput,
  FieldRegistryEntry,
} from "../types.js";
import type {
  InstructionPackTrace,
  ResolvedInstructionPack,
} from "../ai/instruction-pack-provider.js";
import { buildSystemPrompt } from "../ai/system-prompt.js";
import {
  validateSectionIntelligence,
  type SectionIntelligenceResult,
} from "../ai/section-intelligence.js";
import { serializeBuilderStateForPrompt } from "../ai/puck-schema.js";
import { autoWire, bindingsToRecord } from "../ai/auto-wirer.js";
import {
  createVariantGenerator,
  type VariantGenerationRequest,
  type VariantGenerationResult,
  VARIANT_RULE_TYPES,
} from "../dynamic-content/index.js";
import { sanitizePuckData } from "../security/sanitizer.js";
import { puckDataSchema } from "../validation/puck-json.js";
import {
  generatePageInputSchema,
  refinePageInputSchema,
  generateSectionInputSchema,
  aiGenerationResponseSchema,
  validateAiGenerationAgainstRegistry,
  type GeneratePageInput,
  type RefinePageInput,
  type GenerateSectionInput,
} from "../validation/page-config.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AiGatewayRequest {
  taskType:
    | "page_generation"
    | "page_refinement"
    | "section_generation"
    | "blog_generation";
  systemPrompt: string;
  userPrompt: string;
  imageUrl?: string;
  stream: boolean;
  /** Tenant ID — required for ai-gateway routing and compliance */
  tenantId: string;
  /** Actor/user ID — required for ai-gateway audit trail */
  userId: string;
}

export interface AiGatewayResponse {
  /** Raw JSON string from AI */
  content: string;
  /** Token usage stats */
  usage?: { inputTokens: number; outputTokens: number };
}

export interface GenerationResult {
  /** Validated and sanitized Puck JSON */
  puckData: Record<string, unknown>;
  /** Auto-wired field bindings */
  bindings: Record<string, unknown>;
  /** Fields that could not be auto-wired */
  unmappedFields: Array<{ componentId: string; fieldName: string }>;
  /** Token usage from AI gateway */
  usage?: { inputTokens: number; outputTokens: number };
  /** Image replacement credits (Unsplash attribution) */
  imageCredits?: Array<{ url: string; credit: string; creditUrl: string }>;
  /** SEO validation result (post-generation) */
  seoValidation?: SeoValidationResult;
  /** Applied instruction pack trace for auditability */
  instructionPack?: InstructionPackTrace;
  /** Structured per-section findings grounded in canonical section rules */
  sectionValidation?: SectionIntelligenceResult;
}

export interface GenerationServiceDeps {
  /** Send request to AI gateway and get response */
  callAiGateway(request: AiGatewayRequest): Promise<AiGatewayResponse>;
  /** Get canonical style context for the tenant and optional site */
  getBrandContext(tenantId: string, input?: Record<string, unknown>): Promise<string>;
  /** Get field registry context for the tenant */
  getFieldRegistryContext(tenantId: string): Promise<string>;
  /** Get field registry entries for auto-wiring */
  getFieldRegistryEntries(tenantId: string): Promise<FieldRegistryEntry[]>;
  /** Emit CQRS event */
  emitEvent(input: EmitEventInput): Promise<void>;
  /** Resolve the applied instruction pack for a tenant/page type (optional) */
  getInstructionPack?(tenantId: string, pageType?: string, locale?: string): Promise<ResolvedInstructionPack | null>;
  /** Resolve instruction-pack prompt text for variant generation (optional) */
  getInstructionPackPrompt?(tenantId: string): Promise<string | null>;
  /** Replace placeholder images with stock photos (optional — e.g. Unsplash) */
  replaceImages?(puckData: Record<string, unknown>): Promise<ImageReplacementResult>;
  /** Run post-generation SEO validation (optional) */
  validateSeo?(puckData: Record<string, unknown>): SeoValidationResult;
}

export interface ImageReplacementResult {
  replaced: number;
  credits: Array<{ url: string; credit: string; creditUrl: string }>;
}

export interface SeoValidationResult {
  score: number;
  warnings: string[];
  fixes: SeoFix[];
}

export interface SeoFix {
  type: "heading_hierarchy" | "alt_text" | "meta_description" | "aria_label";
  componentIndex?: number;
  description: string;
  applied: boolean;
}

const variantTargetComponentSchema = z.object({
  componentId: z.string().min(1).max(120),
  componentType: z.string().min(1).max(120),
  currentProps: z.record(z.unknown()).refine(
    (props) => JSON.stringify(props).length <= 20_000,
    "Component props exceed maximum size (20KB)",
  ),
});

const campaignDescriptionSchema = z.object({
  name: z.string().min(1).max(120),
  utmValue: z.string().min(1).max(120).regex(/^[a-z0-9_-]+$/i).optional(),
  audience: z.string().min(1).max(240).optional(),
});

const generateVariantsInputSchema = z.object({
  targetComponents: z.array(variantTargetComponentSchema).min(1).max(12),
  campaigns: z.array(campaignDescriptionSchema).min(1).max(8),
  ruleType: z.enum(VARIANT_RULE_TYPES).optional(),
});

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export function createGenerationService(deps: GenerationServiceDeps) {
  return {
    /**
     * Generate a complete page from a text prompt.
     */
    async generate(
      tenantId: string,
      input: GeneratePageInput,
      actorId: string,
    ): Promise<ServiceResult<GenerationResult>> {
      const validation = generatePageInputSchema.safeParse(input);
      if (!validation.success) {
        return {
          error: "validation_error",
          message: validation.error.issues.map((i) => i.message).join("; "),
        };
      }

      const [brandContext, fieldRegistryContext, registryEntries, instructionPack] =
        await Promise.all([
          deps.getBrandContext(tenantId, input),
          deps.getFieldRegistryContext(tenantId),
          deps.getFieldRegistryEntries(tenantId),
          deps.getInstructionPack
            ? deps.getInstructionPack(tenantId, input.pageType, input.locale)
            : Promise.resolve(null),
        ]);

      const { full: systemPrompt } = buildSystemPrompt({
        brandContext: brandContext || undefined,
        businessContext: input.businessContext,
        fieldRegistryContext: fieldRegistryContext || undefined,
        pageType: input.pageType,
        outputKey: input.outputKey,
        locale: input.locale,
        instructionPack: instructionPack || undefined,
        mode: "generate",
      });

      const result = await processAiRequest(
        deps,
        {
          taskType: "page_generation",
          systemPrompt,
          userPrompt: input.prompt,
          imageUrl: input.imageUrl,
          stream: input.stream ?? true,
          tenantId,
          userId: actorId,
        },
        registryEntries,
        instructionPack?.trace,
      );

      if (result.error) {
        await deps.emitEvent({
          tenantId,
          eventType: "generation.failed",
          entityType: "generation",
          entityId: tenantId,
          payload: {
            promptLength: input.prompt.length,
            error: result.error,
            instructionPack: instructionPack?.trace ?? null,
          },
          origin: "ai",
          actorId,
        });
        return result;
      }

      if (!result.data) {
        return { error: "generation_error", message: "AI generation returned empty result" };
      }

      await deps.emitEvent({
        tenantId,
        eventType: "generation.completed",
        entityType: "generation",
        entityId: tenantId,
        payload: {
          type: "full",
          ai_generated: true,
          taskType: "page_generation",
          promptLength: input.prompt.length,
          outputKey: input.outputKey ?? null,
          pageType: input.pageType,
          componentCount: countComponents(result.data.puckData),
          unmappedCount: result.data.unmappedFields.length,
          instructionPack: result.data.instructionPack ?? null,
        },
        origin: "ai",
        actorId,
      });

      return result;
    },

    /**
     * Refine an existing page with instructions.
     */
    async refine(
      tenantId: string,
      input: RefinePageInput,
      actorId: string,
    ): Promise<ServiceResult<GenerationResult>> {
      const validation = refinePageInputSchema.safeParse(input);
      if (!validation.success) {
        return {
          error: "validation_error",
          message: validation.error.issues.map((i) => i.message).join("; "),
        };
      }

      const [brandContext, fieldRegistryContext, registryEntries, instructionPack] =
        await Promise.all([
          deps.getBrandContext(tenantId, input),
          deps.getFieldRegistryContext(tenantId),
          deps.getFieldRegistryEntries(tenantId),
          deps.getInstructionPack
            ? deps.getInstructionPack(tenantId, input.pageType)
            : Promise.resolve(null),
        ]);

      const { full: systemPrompt } = buildSystemPrompt({
        brandContext: brandContext || undefined,
        fieldRegistryContext: fieldRegistryContext || undefined,
        pageType: input.pageType,
        outputKey: input.outputKey,
        instructionPack: instructionPack || undefined,
        mode: "refine",
      });

      const puckValidation = puckDataSchema.safeParse(input.currentPuckData);
      if (!puckValidation.success) {
        return {
          error: "invalid_puck_data",
          message: "Current page data is structurally invalid",
        };
      }

      const userPrompt = [
        serializeBuilderStateForPrompt(puckValidation.data),
        "",
        "Current page JSON:",
        JSON.stringify(input.currentPuckData, null, 2),
        "",
        "Modifications requested:",
        input.instructions,
        "",
        "Refinement requirements:",
        "- Update existing blocks by props.id when possible",
        "- Preserve untouched block ids and valid props",
        "- Keep output fully valid Puck JSON using only canonical component types",
      ].join("\n");

      const result = await processAiRequest(
        deps,
        {
          taskType: "page_refinement",
          systemPrompt,
          userPrompt,
          stream: true,
          tenantId,
          userId: actorId,
        },
        registryEntries,
        instructionPack?.trace,
      );

      if (result.error) {
        await deps.emitEvent({
          tenantId,
          eventType: "generation.failed",
          entityType: "generation",
          entityId: input.pageId ?? tenantId,
          payload: {
            instructionsLength: input.instructions.length,
            outputKey: input.outputKey ?? null,
            pageType: input.pageType,
            error: result.error,
            instructionPack: instructionPack?.trace ?? null,
          },
          origin: "ai",
          actorId,
        });
      } else {
        await deps.emitEvent({
          tenantId,
          eventType: "generation.completed",
          entityType: "generation",
          entityId: input.pageId ?? tenantId,
          payload: {
            type: "refinement",
            ai_generated: true,
            taskType: "page_refinement",
            instructionsLength: input.instructions.length,
            outputKey: input.outputKey ?? null,
            pageType: input.pageType,
            instructionPack: result.data?.instructionPack ?? null,
          },
          origin: "ai",
          actorId,
        });
      }

      return result;
    },

    /**
     * Generate a single section/component.
     */
    async generateSection(
      tenantId: string,
      input: GenerateSectionInput,
      actorId: string,
    ): Promise<ServiceResult<GenerationResult>> {
      const validation = generateSectionInputSchema.safeParse(input);
      if (!validation.success) {
        return {
          error: "validation_error",
          message: validation.error.issues.map((i) => i.message).join("; "),
        };
      }

      const [brandContext, registryEntries] = await Promise.all([
        deps.getBrandContext(tenantId, input),
        deps.getFieldRegistryEntries(tenantId),
      ]);

      const { full: systemPrompt } = buildSystemPrompt({
        brandContext: brandContext || undefined,
        mode: "section",
      });

      const userPrompt = [
        `Generate a single "${input.componentType}" component.`,
        `Context: ${input.context}`,
        input.pageContext
          ? `Existing page context: ${JSON.stringify(input.pageContext)}`
          : "",
      ]
        .filter(Boolean)
        .join("\n");

      const result = await processAiRequest(
        deps,
        {
          taskType: "section_generation",
          systemPrompt,
          userPrompt,
          stream: false,
          tenantId,
          userId: actorId,
        },
        registryEntries,
      );

      if (result.error) {
        await deps.emitEvent({
          tenantId,
          eventType: "generation.failed",
          entityType: "generation",
          entityId: tenantId,
          payload: { componentType: input.componentType, error: result.error },
          origin: "ai",
          actorId,
        });
      } else {
        await deps.emitEvent({
          tenantId,
          eventType: "generation.completed",
          entityType: "generation",
          entityId: tenantId,
          payload: {
            type: "section",
            ai_generated: true,
            taskType: "section_generation",
            componentType: input.componentType,
          },
          origin: "ai",
          actorId,
        });
      }

      return result;
    },

    /**
     * Generate per-block audience variants grounded in the current builder state.
     */
    async generateVariants(
      tenantId: string,
      input: VariantGenerationRequest,
      actorId: string,
    ): Promise<ServiceResult<VariantGenerationResult>> {
      const validation = generateVariantsInputSchema.safeParse(input);
      if (!validation.success) {
        return {
          error: "validation_error",
          message: validation.error.issues.map((i) => i.message).join("; "),
        };
      }

      const [brandContext, instructionPrompt] = await Promise.all([
        deps.getBrandContext(tenantId, validation.data),
        deps.getInstructionPackPrompt
          ? deps.getInstructionPackPrompt(tenantId)
          : Promise.resolve(null),
      ]);

      const generator = createVariantGenerator({
        generateVariantContent: async (prompt) => {
          const response = await callAiForVariants(
            deps,
            tenantId,
            actorId,
            prompt,
            brandContext,
            instructionPrompt,
          );

          return response.data;
        },
      });

      try {
        const result = await generator.generate(validation.data);

        await deps.emitEvent({
          tenantId,
          eventType: "generation.completed",
          entityType: "generation",
          entityId: tenantId,
          payload: {
            type: "variants",
            ai_generated: true,
            taskType: "variant_generation",
            targetComponentCount: validation.data.targetComponents.length,
            campaignCount: validation.data.campaigns.length,
            totalVariants: result.totalVariants,
          },
          origin: "ai",
          actorId,
        });

        return { data: result };
      } catch (err) {
        const message = err instanceof Error ? err.message : "AI variant generation failed";

        await deps.emitEvent({
          tenantId,
          eventType: "generation.failed",
          entityType: "generation",
          entityId: tenantId,
          payload: {
            type: "variants",
            targetComponentCount: validation.data.targetComponents.length,
            campaignCount: validation.data.campaigns.length,
            error: message,
          },
          origin: "ai",
          actorId,
        });

        if (message === "AI response was not valid JSON") {
          return { error: "ai_parse_error", message };
        }

        return {
          error: message.startsWith("AI response did not match")
            ? "ai_validation_error"
            : "ai_gateway_error",
          message,
        };
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Shared processing pipeline
// ---------------------------------------------------------------------------

async function processAiRequest(
  deps: GenerationServiceDeps,
  request: AiGatewayRequest,
  registryEntries: FieldRegistryEntry[],
  instructionPack?: InstructionPackTrace,
): Promise<ServiceResult<GenerationResult>> {
  let response: AiGatewayResponse;

  try {
    response = await deps.callAiGateway(request);
  } catch (err) {
    const message = err instanceof Error ? err.message : "AI gateway request failed";
    return { error: "ai_gateway_error", message };
  }

  // Parse AI response as JSON
  let parsed: unknown;
  try {
    parsed = JSON.parse(response.content);
  } catch {
    return {
      error: "ai_parse_error",
      message: "AI response was not valid JSON",
    };
  }

  // Validate structure (aiGenerationResponseSchema is a superset of puckDataSchema —
  // requires id on every component and at least one content item)
  const structureValidation = aiGenerationResponseSchema.safeParse(parsed);
  if (!structureValidation.success) {
    return {
      error: "ai_validation_error",
      message: structureValidation.error.issues.map((i) => i.message).join("; "),
    };
  }

  // Use Zod-validated data (not raw parsed) for type-safe downstream processing
  const validatedData = structureValidation.data;
  const duplicateIds = findDuplicateComponentIds(validatedData as Record<string, unknown>);
  if (duplicateIds.length > 0) {
    return {
      error: "ai_validation_error",
      message: `Duplicate component ids are not allowed: ${duplicateIds.join(", ")}`,
    };
  }

  const registryIssues = validateAiGenerationAgainstRegistry(validatedData);
  if (registryIssues.length > 0) {
    return {
      error: "ai_validation_error",
      message: registryIssues.join("; "),
    };
  }

  // Sanitize (XSS prevention)
  const sanitized = sanitizePuckData(
    validatedData as Record<string, unknown>,
  );

  // Auto-wire form fields using validated structure
  const wireResult = autoWire(
    {
      content: validatedData.content as Array<{ type: string; props: Record<string, unknown> }>,
      zones: validatedData.zones as Record<string, Array<{ type: string; props: Record<string, unknown> }>> | undefined,
    },
    registryEntries,
  );

  // Post-processing: replace placeholder images with stock photos
  let imageCredits: Array<{ url: string; credit: string; creditUrl: string }> | undefined;
  if (deps.replaceImages) {
    try {
      const imageResult = await deps.replaceImages(sanitized);
      if (imageResult.replaced > 0) {
        imageCredits = imageResult.credits;
      }
    } catch {
      // Image replacement is best-effort — never block generation
    }
  }

  // Post-processing: SEO validation and auto-fix
  let seoValidation: SeoValidationResult | undefined;
  if (deps.validateSeo) {
    try {
      seoValidation = deps.validateSeo(sanitized);
    } catch {
      // SEO validation is best-effort — never block generation
    }
  }

  let sectionValidation: SectionIntelligenceResult | undefined;
  try {
    sectionValidation = validateSectionIntelligence(sanitized as {
      content?: Array<{ type?: string; props?: Record<string, unknown> }>;
      zones?: Record<string, Array<{ type?: string; props?: Record<string, unknown> }>>;
    });
  } catch {
    // Section validation is best-effort — never block generation
  }

  return {
    data: {
      puckData: sanitized,
      bindings: bindingsToRecord(wireResult.bindings),
      unmappedFields: wireResult.unmapped,
      usage: response.usage,
      imageCredits,
      seoValidation,
      instructionPack,
      sectionValidation,
    },
  };
}

const variantAiResponseSchema = z.array(z.object({
  componentId: z.string().min(1),
  componentType: z.string().min(1),
  campaignName: z.string().min(1),
  overrides: z.record(z.unknown()),
}));

async function callAiForVariants(
  deps: GenerationServiceDeps,
  tenantId: string,
  actorId: string,
  prompt: string,
  brandContext: string,
  instructionPrompt: string | null,
): Promise<{ data: Array<{
  componentId: string;
  componentType: string;
  campaignName: string;
  overrides: Record<string, unknown>;
}> }> {
  let response: AiGatewayResponse;

  try {
    response = await deps.callAiGateway({
      taskType: "page_refinement",
      systemPrompt: buildVariantSystemPrompt(brandContext, instructionPrompt),
      userPrompt: prompt,
      stream: false,
      tenantId,
      userId: actorId,
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : "AI gateway request failed";
    throw new Error(message);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(response.content);
  } catch {
    throw new Error("AI response was not valid JSON");
  }

  const validation = variantAiResponseSchema.safeParse(parsed);
  if (!validation.success) {
    throw new Error(
      `AI response did not match the variant contract: ${validation.error.issues.map((i) => i.message).join("; ")}`,
    );
  }

  return { data: validation.data };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function countComponents(puckData: Record<string, unknown>): number {
  let count = 0;
  if (Array.isArray(puckData.content)) {
    count += puckData.content.length;
  }
  if (puckData.zones && typeof puckData.zones === "object") {
    for (const zone of Object.values(puckData.zones as Record<string, unknown[]>)) {
      if (Array.isArray(zone)) {
        count += zone.length;
      }
    }
  }
  return count;
}

function buildVariantSystemPrompt(
  brandContext: string,
  instructionPrompt: string | null,
): string {
  return [
    "You generate audience-specific variants for an EXISTING Ease page-builder canvas.",
    "This is assistance only. The user will review the variants before saving them.",
    brandContext ? `Brand context:\n${brandContext}` : "",
    instructionPrompt ? `Instruction pack:\n${instructionPrompt}` : "",
    "Rules:",
    "- Output JSON only.",
    "- Every item must contain componentId, componentType, campaignName, overrides.",
    "- Keep overrides grounded in the provided current props for that exact componentId.",
    "- Never include id or variants inside overrides.",
    "- Never invent unsupported component props or fake business facts.",
    "- Prefer concise copy changes over structural rewrites.",
  ].filter(Boolean).join("\n\n");
}

function findDuplicateComponentIds(puckData: Record<string, unknown>): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  const collect = (components: unknown) => {
    if (!Array.isArray(components)) {
      return;
    }

    for (const component of components) {
      if (!component || typeof component !== "object") {
        continue;
      }

      const props = (component as { props?: Record<string, unknown> }).props;
      const componentId = typeof props?.id === "string" ? props.id : null;
      if (!componentId) {
        continue;
      }

      if (seen.has(componentId)) {
        duplicates.add(componentId);
      } else {
        seen.add(componentId);
      }
    }
  };

  collect(puckData.content);

  if (puckData.zones && typeof puckData.zones === "object") {
    for (const zone of Object.values(puckData.zones as Record<string, unknown>)) {
      collect(zone);
    }
  }

  return [...duplicates];
}
